import React, { useEffect, useState, useCallback } from 'react'
import {
  Divider,
  Typography,
  Spin,
  Select,
  message,
  Empty,
  Collapse,
  Button,
  Form,
  Modal,
  Tag,
  Upload,
  Input,
} from 'antd'
import {
  EditOutlined,
  DeleteOutlined,
  ColumnHeightOutlined,
} from '@ant-design/icons'
import { getCharacters } from 'src/api/user'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import s from './index.module.scss'
import { fetchCountryProvider } from 'src/api/authen'
import {
  getLayout,
  getLayoutSection,
  handleLayoutOrder,
  handleSectionOrder,
  saveEditLayout,
  saveAddSectionItemLayout,
  deleteSectionItem,
  deleteSection,
  addSectionItemLayout,
  editSectionContent,
} from 'src/api/character'
import { manualFetch } from 'src/utils/fetch'
import Carousel from './carousel'
import CarouselItem from './carousel-item'
import SectionFormModal from './SectionFormModal'
import { NULL } from 'sass'
const { Title } = Typography
const { Panel } = Collapse

const LayoutCharacter: React.FC = () => {
  const [providerCountry, setProviderCountry] = useState('TN')
  const [layoutId, setLayoutId] = useState('')
  const [currentSectionId, setCurrentSectionId] = useState('')
  const [uploadedImage, setUploadedImage] = useState<File | null>(null)
  const [previewSrc, setPreviewSrc] = useState<string>('')
  const [characters, setCharacters] = useState<any[]>([])
  const [countries, setCountries] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [layouts, setLayouts] = useState<any[]>([])
  const [openKeys, setOpenKeys] = useState<string[]>([])
  const [isEditModalVisible, setIsEditModalVisible] = useState(false)
  const [isAddSectionModalVisible, setIsAddSectionModalVisible] =
    useState(false)
  const [isAddItemSectionModalVisible, setIsAddItemSectionModalVisible] =
    useState(false)
  const [sectionItemsMap, setSectionItemsMap] = useState<Record<string, any[]>>(
    {}
  )
  const [editingSection, setEditingSection] = useState<any>(null)
  const [form] = Form.useForm()
  const [selectedEntityType, setSelectedEntityType] = useState<string | null>(
    null
  )
  const [selectedCharacter, setSelectedCharacter] = useState<any | null>(null)

  const [entityOptions, setEntityOptions] = useState<any[]>([])
  const [selectedEntity, setSelectedEntity] = useState<string | null>(null)
  const [imageCache, setImageCache] = useState<Record<string, string>>({})
  const [moveModalVisible, setMoveModalVisible] = useState(false)
  const [moveFromIndex, setMoveFromIndex] = useState<number | null>(null)
  const [moveToIndex, setMoveToIndex] = useState<number | null>(null)

  const [itemTitle, setItemTitle] = useState('')
  const [itemImage, setItemImage] = useState<File | null>(null)
  const [itemImagePreview, setItemImagePreview] = useState<string>('')
  const [isEditItemModalVisible, setIsEditItemModalVisible] = useState(false)
  const [editingItem, setEditingItem] = useState<any>(null)

  const fetchImage = async (url: string): Promise<string> => {
    if (!url) return ''
    if (imageCache[url]) {
      return imageCache[url]
    }
    try {
      const response = await manualFetch(url, {
        headers: { accept: 'image/*' },
      })
      const blob = await response.blob()
      const objectUrl = URL.createObjectURL(blob)
      setImageCache((prev) => ({ ...prev, [url]: objectUrl }))
      return objectUrl
    } catch (error) {
      console.error('Failed to fetch image:', error)
      return ''
    }
  }
  const isLargeSection = (section?: any) => section?.thumbnailSize === 'LARGE'

  const handleItemImageUpload = (info: any) => {
    const file = info.file.originFileObj || info.file
    if (!file) return

    setItemImage(file)
    setItemImagePreview(URL.createObjectURL(file))
  }

  const fetchCharacters = async () => {
    const response = await getCharacters(0, 1000)
    setCharacters(response.data || [])
  }

  const characterOptions = characters
    .filter((c) => c.enabled)
    .map((c) => ({
      label: c.name,
      value: c.id,
      image: c.image,
    }))

  const openEditItemModal = async (item) => {
    setEditingItem(item)

    setItemTitle(item.icon_title || '')

    setCurrentSectionId(item.section_id)
    if (item.icon_s3_Path) {
      const iconImage = await fetchImage(item.icon_s3_Path)
      setItemImagePreview(iconImage)
    }

    setItemImage(null)
    setIsEditItemModalVisible(true)
  }

  const fetchCountries = async () => {
    try {
      const { data } = await fetchCountryProvider()
      setCountries(
        data.map((item: any) => ({
          label: item.countryName,
          value: item.countryCode,
        }))
      )
    } catch (err) {
      console.error(err)
      message.error('Failed to load countries')
    }
  }

  const fetchSection = useCallback(
    async (sectionId) => {
      try {
        const sectionRes = await getLayoutSection(sectionId)
        const games = sectionRes?.data || []
        const gamesWithImages = await Promise.all(
          games.map(async (game) => ({
            ...game,
            localThumb: await fetchImage(game.thumbnailS3Path),
          }))
        )
        setLayouts((prev) =>
          prev.map((s) =>
            s.sectionId === sectionId ? { ...s, games: gamesWithImages } : s
          )
        )

        setSectionItemsMap((prev) => ({
          ...prev,
          [sectionId]: games.map((g) => ({
            type: g.type,
            entityId: g.entityId || g.id,
          })),
        }))
      } catch (err) {
        console.error('Failed to fetch section:', err)
        message.error('Failed to reload section')
      }
    },
    [fetchImage]
  )

  const fetchLayoutData = useCallback(async () => {
    try {
      setLoading(true)
      const layoutRes = await getLayout(providerCountry)
      setLayoutId(layoutRes.id)

      const layoutItems = layoutRes?.items || []
      const updatedLayouts: any[] = []
      for (const item of layoutItems) {
        if (!item.sectionId && item.type !== 'DYNAMIC') {
          updatedLayouts.push({ ...item, games: [] })
          continue
        }

        const sectionRes = await getLayoutSection(item.sectionId)
        const games = sectionRes?.data || []

        updatedLayouts.push({
          ...item,
          games,
        })

        setSectionItemsMap((prev) => ({
          ...prev,
          [item.sectionId]: games.map((g) => ({
            type: g.type,
            entityId: g.entityId || g.id,
          })),
        }))
      }

      setLayouts(updatedLayouts)

      const dynamicKeys = updatedLayouts
        .filter((sec) => sec.type === 'DYNAMIC')
        .map((sec) => sec.id)

      setOpenKeys(dynamicKeys)
      for (const item of updatedLayouts) {
        if (!item.games?.length) continue

        item.games.forEach(async (game, idx) => {
          try {
            const localThumb = await fetchImage(game.thumbnailS3Path)
            setLayouts((prev) =>
              prev.map((lay) =>
                lay.id === item.id
                  ? {
                      ...lay,
                      games: lay.games.map((g, i) =>
                        i === idx ? { ...g, localThumb } : g
                      ),
                    }
                  : lay
              )
            )
          } catch (err) {
            console.warn('Image load failed:', game.thumbnailS3Path)
          }
        })
      }
    } catch (err) {
      console.error(err)
      message.error('Failed to fetch layout data')
    } finally {
      setLoading(false)
    }
  }, [providerCountry])

  const handleReorder = async (section, dragId, dropId) => {
    try {
      const dragIndex = section.games.findIndex((g) => g.entityId === dragId)
      const dropIndex = section.games.findIndex((g) => g.entityId === dropId)

      if (dragIndex === -1 || dropIndex === -1) return

      setLayouts((prev) =>
        prev.map((s) => {
          if (s.sectionId !== section.sectionId) return s

          const reordered = [...s.games]
          const [dragged] = reordered.splice(dragIndex, 1)
          reordered.splice(dropIndex, 0, dragged)

          return {
            ...s,
            games: reordered,
          }
        })
      )

      await handleSectionOrder(section.sectionId, dragIndex, dropIndex)

      message.success('Section item order updated')

      fetchSection(section.sectionId)
    } catch (err) {
      console.error('Failed to reorder section item', err)
      message.error('Failed to update order')
      fetchSection(section.sectionId)
    }
  }

  const performMove = async (prevIdx, newIdx) => {
    if (prevIdx == null || newIdx == null) return
    if (prevIdx === newIdx) {
      setMoveModalVisible(false)
      return
    }
    setLayouts((prev) => {
      const copy = [...prev]
      const [item] = copy.splice(prevIdx, 1)
      copy.splice(newIdx, 0, item)
      return copy
    })

    setMoveModalVisible(false)

    try {
      const res = await handleLayoutOrder(layoutId, prevIdx, newIdx)
      if (res === 'success') {
        message.success('Panel order updated')
        await fetchLayoutData()
      } else {
        throw new Error('API returned failed')
      }
    } catch (err) {
      console.error('Failed to save new order', err)
      message.error('Failed to save new order — reloading layout')
      await fetchLayoutData()
    } finally {
      setMoveFromIndex(null)
      setMoveToIndex(null)
    }
  }

  const openMoveModal = (sectionIndex) => {
    setMoveFromIndex(sectionIndex)
    setMoveToIndex(sectionIndex)
    setMoveModalVisible(true)
  }

  const confirmDeleteSection = (section) => {
    Modal.confirm({
      title: 'Delete Section?',
      content: `Are you sure you want to delete the section "${
        section.title || section.type
      }"?`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => handleDeleteSection(section),
    })
  }

  const handleDeleteSection = async (section) => {
    try {
      await deleteSection(layoutId, section.id)
      setLayouts((prev) => prev.filter((s) => s.id !== section.id))
      setSectionItemsMap((prev) => {
        const copy = { ...prev }
        delete copy[section.id]
        return copy
      })
      message.success('Section deleted successfully')
    } catch (err) {
      console.error('Failed to delete section:', err)
      message.error('Failed to delete section')
    }
  }

  const handleDelete = async (itemToDelete, section) => {
    try {
      const updatedSection = section.games
        .filter((g) => g.entityId !== itemToDelete.id)
        .map((g) => ({
          entityId: g.entityId,
          type: g.type,
        }))

      const index = section.games.findIndex(
        (g) => g.entityId === itemToDelete.id
      )
      await deleteSectionItem(section.sectionId, index)
      message.success('Deleted Section Item Successfully')
      fetchSection(section.sectionId)
      setSectionItemsMap((prev) => ({
        ...prev,
        [section.sectionId]: updatedSection,
      }))
    } catch (err) {
      console.error('Failed to delete section item:', err)
      message.error('Failed to delete section item')
    }
  }

  const handleCancelAddItemSection = () => {
    setIsAddItemSectionModalVisible(false)
    setEntityOptions([])
    resetAddItemState()
  }

  const getCharacterImage = async (character: any) => {
    if (!character?.image) return ''

    if (imageCache[character.image]) {
      return imageCache[character.image]
    }

    const localImg = await fetchImage(character.image)
    return localImg
  }

  const handleAddItem = (section) => {
    resetAddItemState()
    setEditingItem(null)
    setEditingSection(section)
    setCurrentSectionId(section.sectionId)
    setIsAddItemSectionModalVisible(true)
  }

  const handleSaveAddItemSection = async () => {
    if (isLargeSection(editingSection)) {
      if (!itemTitle || !itemImage) {
        message.error('Title and Image are required for LARGE section items')
        return
      }
    }
    if (!selectedEntity || !currentSectionId) return

    const items = sectionItemsMap[currentSectionId] || []

    const newItem = {
      entityId: selectedEntity,
    }

    setSectionItemsMap((prev) => {
      const existing = prev[currentSectionId] || []
      const alreadyExists = existing.some(
        (item) => item.entityId === newItem.entityId
      )
      if (alreadyExists) return prev
      return {
        ...prev,
        [currentSectionId]: [...existing, newItem],
      }
    })

    try {
      const updatedItems = [...(items || [])]
      if (!updatedItems.some((item) => item.entityId === newItem.entityId)) {
        updatedItems.push(newItem)
      }

      await addSectionItemLayout(
        currentSectionId,
        selectedEntity,
        itemTitle,
        itemImage
      )

      message.success('Added Section Item Successfully')

      setIsAddItemSectionModalVisible(false)
      setSelectedEntityType(null)
      setSelectedEntity(null)
      fetchSection(currentSectionId)
      resetAddItemState()
    } catch (err) {
      console.error('Failed to save section items:', err)
      message.error('Failed to save section items')
    }
  }

  const handleSaveEditItem = async () => {
    const section = layouts.find((s) => s.sectionId === currentSectionId)
    if (isLargeSection(section)) {
      if (!itemTitle || (!itemImage && !itemImagePreview)) {
        message.error('Title and Image are required')
        return
      }
    }
    try {
      await editSectionContent(
        editingItem.section_id,
        editingItem.id,
        itemTitle,
        itemImage
      )

      message.success('Content updated')
      setIsEditItemModalVisible(false)
      fetchSection(currentSectionId)
      resetAddItemState()
    } catch (err) {
      message.error('Failed to update content')
    }
  }

  const handleEditSection = async (section: any, e: React.MouseEvent) => {
    setCurrentSectionId(section.sectionId)
    e.stopPropagation()
    setEditingSection(section)
    setIsEditModalVisible(true)
    form.setFieldsValue({
      name: section.name,
      title: section.title,
      description: section.description,
      iconS3Path: section.iconS3Path,
      thumbnailSize: section.thumbnailSize,
      type: section.type,
      enable: section.enable !== false,
      viewAll: section.viewAll !== false,
      sectionId: section.sectionId,
      autoShuffle:
        section.autoShuffle !== null && section.autoShuffle !== false,
    })
    setPreviewSrc('')
    setUploadedImage(null)
    if (section.iconS3Path) {
      const objUrl = await fetchImage(section.iconS3Path)
      setPreviewSrc(objUrl)
    }
  }

  const handleImageUpload = (info: any) => {
    const file = info.file.originFileObj || info.file
    if (file) {
      const localPreview = URL.createObjectURL(file)
      setUploadedImage(file)
      setPreviewSrc(localPreview)
      form.setFieldsValue({ iconS3Path: '' })
    }
  }

  const handleSectionSubmit = async (values: any) => {
    try {
      if (editingSection && isEditModalVisible) {
        await saveEditLayout(layoutId, editingSection.id, values, uploadedImage)
      } else {
        await saveEditLayout(layoutId, null, values, uploadedImage)
      }

      setIsEditModalVisible(false)
      setIsAddSectionModalVisible(false)
      setUploadedImage(null)
      setPreviewSrc('')
      fetchLayoutData()
    } catch (err) {
      console.error(err)
      message.error('Failed to save section')
    }
  }

  const resetAddItemState = () => {
    setSelectedEntity(null)
    setSelectedEntityType(null)
    setSelectedCharacter(null)
    setItemTitle('')
    setItemImage(null)

    setItemImagePreview('')
  }

  const clearImage = () => {
    form.setFieldsValue({ iconS3Path: '' })
    setUploadedImage(null)
    setPreviewSrc('')
  }

  useEffect(() => {
    fetchCountries()
    fetchLayoutData()
  }, [fetchLayoutData])

  useEffect(() => {
    fetchCharacters()
  }, [])

  return (
    <Spin spinning={loading}>
      <div className={s.layoutDiscovery}>
        <Title level={3}>Layout</Title>
        <Divider />
        <div className={s.layoutControls}>
          <div>
            <Select
              placeholder="Select Country"
              value={providerCountry}
              onChange={setProviderCountry}
              options={countries}
              style={{ width: 180 }}
            />
          </div>
          <Button
            onClick={() => {
              form.resetFields()
              setUploadedImage(null)
              setPreviewSrc('')
              setEditingSection(null)
              setIsAddSectionModalVisible(true)
            }}
            type="primary"
            className="btn-primary"
            size="middle"
            style={{ width: 140 }}
          >
            + Add Section
          </Button>
        </div>
        <Divider />
        <DndProvider backend={HTML5Backend}>
          <div style={{ textAlign: 'right', marginBottom: 16 }}></div>

          {layouts.length > 0 ? (
            <Collapse
              activeKey={openKeys}
              onChange={(keys) => setOpenKeys(keys as string[])}
              className={s.layoutCollapse}
              ghost
            >
              {layouts.map((section) => (
                <Panel
                  key={section.id}
                  header={
                    <div className={s.panelHeader}>
                      <Title level={4} className={s.sectionTitle}>
                        {section.title}
                        {section.games?.length > 0 && (
                          <span className={s.gameCount}>
                            ({section.games.length})
                          </span>
                        )}
                      </Title>
                      <div
                        className={s.headerActions}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          display: 'flex',
                          gap: 8,
                          alignItems: 'center',
                        }}
                      >
                        <Button
                          type="text"
                          style={{ color: '#1790ae' }}
                          icon={<EditOutlined />}
                          onClick={(e) => handleEditSection(section, e)}
                        />
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          position: 'absolute',
                          right: 10,
                          top: '50%',
                          transform: 'translateY(-50%)',
                        }}
                      >
                        <Button
                          type="text"
                          onClick={() => confirmDeleteSection(section)}
                          style={{ color: 'red' }}
                          title="Delete section"
                          icon={<DeleteOutlined />}
                        />

                        <Button
                          type="text"
                          onClick={(e) => {
                            e.stopPropagation()
                            const idx = layouts.findIndex(
                              (l) => l.id === section.id
                            )
                            openMoveModal(
                              idx >= 0 ? idx : layouts.indexOf(section)
                            )
                          }}
                          style={{ color: '#1790ae' }}
                          title="Move section"
                          icon={<ColumnHeightOutlined />}
                        />
                        <Tag
                          color={section.enable ? 'green' : 'red'}
                          style={{
                            margin: 0,
                            fontWeight: 500,
                          }}
                        >
                          {section.enable ? 'Enabled' : 'Disabled'}
                        </Tag>
                      </div>
                    </div>
                  }
                  showArrow={true}
                  className={s.collapsePanel}
                >
                  {section.type === 'DYNAMIC' ? (
                    <div className={s.carouselWrapper}>
                      <Carousel
                        name={section.title}
                        sectionItem={section}
                        onAddClick={(item) => handleAddItem(item)}
                      >
                        {section.games?.length > 0 ? (
                          section.games.map((game: any) => (
                            <CarouselItem
                              key={game.entityId}
                              layoutCard={{
                                id: game.entityId,
                                section_id: section.sectionId,
                                title: game.name,
                                cover_url: game.localThumb,
                                number_tracks: game.totalTracks || 0,
                                icon_s3_Path: game.iconS3Path,
                                icon_title: game.title,
                              }}
                              thumbnailSize={section.thumbnailSize}
                              categoryId={section.id}
                              onDrop={(dragId, dropId) => {
                                handleReorder(section, dragId, dropId)
                              }}
                              onRemove={(item) => handleDelete(item, section)}
                              onEdit={(item) => openEditItemModal(item)}
                            />
                          ))
                        ) : (
                          <div
                            style={{
                              padding: '60px 20px',
                              textAlign: 'center',
                            }}
                          ></div>
                        )}
                      </Carousel>
                    </div>
                  ) : (
                    <span style={{ color: 'white', font: 'caption' }}>
                      Static Data
                    </span>
                  )}
                </Panel>
              ))}
            </Collapse>
          ) : (
            <Empty
              description="No layout data available"
              className={s.noData}
            />
          )}
        </DndProvider>

        <SectionFormModal
          visible={isEditModalVisible || isAddSectionModalVisible}
          mode={isEditModalVisible ? 'edit' : 'add'}
          form={form}
          uploadedImage={uploadedImage}
          previewSrc={previewSrc}
          onCancel={() => {
            form.resetFields()
            setUploadedImage(null)
            setPreviewSrc('')
            setIsEditModalVisible(false)
            setIsAddSectionModalVisible(false)
            setEditingSection(null)
          }}
          onUpload={handleImageUpload}
          onClearImage={clearImage}
          onSubmit={handleSectionSubmit}
          existingSections={layouts}
          isEditingExisting={isEditModalVisible}
        />

        <Modal
          title="Add Section Item"
          visible={isAddItemSectionModalVisible}
          onCancel={handleCancelAddItemSection}
          footer={null}
          width={750}
        >
          <div style={{ display: 'flex', gap: 24 }}>
            <div style={{ flex: 1 }}>
              <Typography.Text strong>Select Character</Typography.Text>

              <Select
                placeholder="Choose character"
                style={{ width: '100%', marginTop: 8 }}
                options={characters
                  .filter((c) => c.enabled)
                  .map((c) => ({
                    label: c.name,
                    value: c.id,
                  }))}
                value={selectedEntity}
                onChange={async (value) => {
                  setSelectedEntity(value)
                  setSelectedEntityType('CHARACTER')

                  const char = characters.find((c) => c.id === value)
                  setSelectedCharacter(char || null)

                  if (char?.image) {
                    const img = await fetchImage(char.image)
                    setItemImagePreview(img)
                  } else {
                    setItemImagePreview('')
                  }
                }}
              />

              {editingSection?.thumbnailSize === 'LARGE' && (
                <>
                  <Divider />

                  <Input
                    placeholder="Enter title..."
                    value={itemTitle}
                    onChange={(e) => setItemTitle(e.target.value)}
                    style={{ marginTop: 8 }}
                  />

                  <Divider />

                  <Upload
                    accept="image/*"
                    maxCount={1}
                    beforeUpload={() => false}
                    onChange={handleItemImageUpload}
                  >
                    <Button style={{ marginTop: 8 }}>Change Icon</Button>
                  </Upload>
                </>
              )}
            </div>

            <div style={{ width: 240, textAlign: 'center' }}>
              {itemImagePreview ? (
                <img
                  src={itemImagePreview}
                  alt="Preview"
                  style={{
                    width: '100%',
                    height: 220,
                    objectFit: 'cover',
                    borderRadius: 12,
                  }}
                />
              ) : (
                <Empty description="No preview available" />
              )}
            </div>
          </div>

          <Divider />

          <div className={s.modalActions}>
            <Button
              type="primary"
              size="middle"
              style={{
                width: 80,
                background: 'linear-gradient(-90deg, #f3109e, #181c84)',
                border: 'none',
                opacity: 0.9,
              }}
              onClick={handleSaveAddItemSection}
              disabled={
                !selectedEntity ||
                (isLargeSection(editingSection) && (!itemTitle || !itemImage))
              }
            >
              Save
            </Button>

            <Button onClick={handleCancelAddItemSection}>Cancel</Button>
          </div>
        </Modal>

        <Modal
          title="Edit Content Item"
          visible={isEditItemModalVisible}
          onCancel={() => {
            setIsEditItemModalVisible(false)
            setEditingItem(null)
            resetAddItemState()
          }}
          onOk={handleSaveEditItem}
          okText="Save"
          width={600}
          okButtonProps={{
            disabled:
              isLargeSection(
                layouts.find((s) => s.sectionId === currentSectionId)
              ) &&
              (!itemTitle || (!itemImage && !itemImagePreview)),
          }}
        >
          <Input
            value={itemTitle}
            onChange={(e) => setItemTitle(e.target.value)}
            style={{ marginTop: 8 }}
            placeholder="Enter title..."
          />
          <Divider />
          <Upload
            accept="image/*"
            maxCount={1}
            beforeUpload={() => false}
            onChange={handleItemImageUpload}
          >
            <Button
              size="middle"
              style={{
                width: 110,
                background: 'linear-gradient(-90deg, #f3109e, #181c84)',
                border: 'none',
                opacity: 0.9,
              }}
            >
              Change Icon
            </Button>
          </Upload>

          {itemImagePreview && (
            <img
              src={itemImagePreview}
              style={{
                width: '20%',
                height: '20%',
                objectFit: 'cover',
                borderRadius: 12,
                marginTop: 10,
              }}
            />
          )}
        </Modal>

        <Modal
          title="Move Section"
          visible={moveModalVisible}
          onCancel={() => {
            setMoveModalVisible(false)
            setMoveFromIndex(null)
            setMoveToIndex(null)
          }}
          onOk={() => {
            if (moveFromIndex !== null && moveToIndex !== null) {
              performMove(moveFromIndex, moveToIndex)
            } else {
              setMoveModalVisible(false)
            }
          }}
          okText="Move"
          cancelText="Cancel"
        >
          <div style={{ marginBottom: 12 }}>
            <strong>Move section:</strong>{' '}
            {moveFromIndex != null
              ? layouts[moveFromIndex]?.title ?? `#${moveFromIndex + 1}`
              : ''}
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: 6 }}>
              Choose target position
            </label>
            <Select
              value={moveToIndex !== null ? moveToIndex : undefined}
              onChange={(val) => setMoveToIndex(val)}
              style={{ width: '100%' }}
              options={layouts.map((l, idx) => ({
                label: `${idx + 1} — ${l.title || `Section ${idx + 1}`}`,
                value: idx,
              }))}
              placeholder="Select position"
            />
          </div>
        </Modal>
      </div>
    </Spin>
  )
}
LayoutCharacter.displayName = 'LayoutCharacter'
export default LayoutCharacter
